import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../api";
import { MIGRATION } from "../../../constants/testIds";
import { Button } from "../../ui/button";
import { SubmitButton } from "../../common/SubmitButton";
import { RollbackDialog } from "../RollbackDialog";

// SCR-P04-15 - a single POST /migration/commit call processes every clean/duplicate row
// server-side (already idempotent/resumable - re-running never double-commits, verified by
// tests/test_phase10_migration_adversarial.py). "Retry commit" just calls the same endpoint
// again, which is exactly how an interrupted commit resumes.
export function CommitStep({ batchId, onDone }) {
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [result, setResult] = useState(null);

  const { data: batch } = useQuery({
    queryKey: ["migration-batch", batchId],
    queryFn: () => api.get(`/migration/${batchId}`).then((r) => r.data),
    enabled: !!batchId,
  });

  const commitMutation = useMutation({
    mutationFn: () => api.post("/migration/commit", { batch_id: batchId }),
    onSuccess: (r) => setResult(r.data),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Commit failed"),
  });

  const rollbackMutation = useMutation({
    mutationFn: () => api.post(`/migration/${batchId}/rollback`),
    onSuccess: () => { toast.success("Rolled back"); setRollbackOpen(false); onDone(); },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Rollback failed"),
  });

  const status = result?.status || batch?.status;
  const canRollback = status === "committed" || status === "committing";

  // Either the just-run commit response, or (resuming a batch that was already committed
  // earlier) a summary derived from the batch's own stored rows.
  const stats = result || (batch && (batch.status === "committed" || batch.status === "committing" || batch.status === "rolled_back") ? {
    committed_now: batch.rows.filter((r) => r.member_ref).length,
    skipped_already_committed: 0,
    skipped_flagged: batch.rows.filter((r) => r.classification === "flagged").length,
    errors: [],
  } : null);

  return (
    <div className="space-y-4">
      {!stats && (
        <div className="solid-surface rounded-[14px] p-8 text-center">
          <p className="text-sm mb-3" style={{ color: "var(--scarlet-text-secondary)" }}>Ready to commit. Clean and duplicate-merged rows become real members; flagged rows are skipped.</p>
          <SubmitButton testId={MIGRATION.commitStartButton} pending={commitMutation.isPending} onClick={() => commitMutation.mutate()} label="Commit migration" />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <div data-testid={MIGRATION.commitResultCommitted} className="solid-surface rounded-[12px] p-4 text-center">
            <p className="text-2xl font-bold" style={{ color: "#3FA65B" }}>{stats.committed_now}</p>
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Committed now</p>
          </div>
          <div data-testid={MIGRATION.commitResultSkipped} className="solid-surface rounded-[12px] p-4 text-center">
            <p className="text-2xl font-bold" style={{ color: "#8B8B92" }}>{stats.skipped_already_committed + stats.skipped_flagged}</p>
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Skipped</p>
          </div>
          <div data-testid={MIGRATION.commitResultErrors} className="solid-surface rounded-[12px] p-4 text-center">
            <p className="text-2xl font-bold" style={{ color: stats.errors.length ? "#D64545" : "#3FA65B" }}>{stats.errors.length}</p>
            <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Errors</p>
          </div>
        </div>
      )}

      {stats?.errors?.length > 0 && (
        <div className="space-y-2">
          <SubmitButton testId="migration-commit-retry-button" pending={commitMutation.isPending} onClick={() => commitMutation.mutate()} label="Retry commit" />
        </div>
      )}

      {status === "committed" && (
        <div className="flex items-center gap-2 text-sm" style={{ color: "#3FA65B" }}>
          <CheckCircle2 size={16} /> Migration committed.
        </div>
      )}

      <div className="flex justify-between">
        {canRollback ? (
          <Button data-testid={MIGRATION.commitRollbackButton} variant="destructive" onClick={() => setRollbackOpen(true)}>Roll back</Button>
        ) : <div />}
        <Button data-testid={MIGRATION.commitDoneButton} onClick={onDone} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>Back to Migration Studio</Button>
      </div>

      <RollbackDialog open={rollbackOpen} onOpenChange={setRollbackOpen} onConfirm={() => rollbackMutation.mutate()} pending={rollbackMutation.isPending} />
    </div>
  );
}
